/**
 * Auto-Init for Browser Bundle
 * Reads script tag attributes and auto-configures LionRapid
 *
 * Usage:
 * <script
 *   src="https://cdn.lionrapid.com/lionrapid.auto.min.js"
 *   data-api-key="pk_live_xxx"
 *   data-default-locale="en"
 *   data-namespace="default"
 *   data-languages="en,es,fr,de"
 * ></script>
 */

import { LionRapidBuilder } from '../builder.js';
import { MemoryRepository } from '../repositories/memory-repository.js';
import { NetworkRepository } from '../repositories/network-repository.js';
import { AutoTranslator } from './auto-translator.js';
import { LanguageSwitcher } from './language-switcher.js';
import { LocalStorageRepository } from './storage/localstorage-repository.js';
import { CacheStorageRepository } from './storage/cache-storage-repository.js';
import { CORE_CACHE_NAMES } from './storage/cache-names.js';
import { DEFAULT_API_BASE_URL } from '../config/api.config.js';
import {
  matchLocale,
  normalizeLocale,
  localeDir,
  LOCALE_TAG_REGEX,
} from '../utils/locale.js';

export interface AutoInitConfig {
  apiKey: string;
  baseUrl: string;
  defaultLocale: string;
  namespace: string;
  languages: string[];
  excludeSelectors: string[];
  autoDetect: boolean;
  mode: 'full' | 'dynamic-only';
  switcherStyle?: string;
  switcherPosition?: string;
  switcherShowFlags?: boolean;
  switcherShowNames?: boolean;
  switcherCustomCss?: string;
  /** Override for the visual editor bundle URL. Defaults to the public CDN.
   *  Self-hosted hosts (e.g. the WordPress plugin) point this at their own
   *  vendored copy so the editor is version-locked to the host, not the CDN. */
  editorCdnUrl?: string;
  /** Offline resilience: add a never-expiring "last known good" cache layer that
   *  is hydrated by successful fetches, so the site keeps translating with the
   *  server down even after the normal caches expire. Default on; disable with
   *  `data-offline="false"`. */
  offline?: boolean;
}

declare global {
  interface Window {
    LionRapid: LionRapidInstance | undefined;
    __lionrapidConfig?: AutoInitConfig;
  }
}

/**
 * Write policy for a browser-embedded key.
 *
 * A publishable `pk_` key gets the DISCOVERY path only: page views sync
 * missing source strings so rows appear in the dashboard (the server's
 * `_SYNC_KEYS` explicitly allows publishable keys on /sync). It can never
 * write translations — the server restricts those to master/editor-session
 * keys (`_WRITE_KEYS`), which is what actually blocks the stored-XSS loop.
 *
 * Anything that isn't a pk_ key is a secret shipped to the page (Phase 18):
 * the network layer refuses to boot at all — the key never goes on the
 * wire — and the refusal is flagged loudly so the integrator notices.
 */
export function resolveWritePolicy(apiKey: string): {
  /** Phase 18: pk_ is the ONLY key that boots the network layer — a non-pk_
   *  value is a secret (or garbage) pasted into public HTML and must never
   *  go on the wire. Cached/offline translations still apply. */
  bootNetwork: boolean;
  syncMissing: { enabled: boolean };
  warnRefused: boolean;
} {
  const isPublishable = apiKey.startsWith('pk_');
  return {
    bootNetwork: isPublishable,
    syncMissing: { enabled: isPublishable },
    warnRefused: !isPublishable,
  };
}

/**
 * The config copy mirrored to `window.__lionrapidConfig` for the editor
 * bundle — with the API key stripped. The key must never sit on `window`
 * where any third-party script on the page could read it; the editor only
 * needs languages/namespace/locale from here.
 */
export function configForWindow(config: AutoInitConfig): AutoInitConfig {
  return { ...config, apiKey: '' };
}

/**
 * Switcher change-handling that survives the boot window. `build()` can take
 * seconds under a failing network (retry backoff); a click in that window
 * must not be silently dropped. The cookie is written and dynamic-only
 * navigation happens immediately (neither needs core); in full mode a click
 * before `attach` is remembered (last one wins) and replayed the moment the
 * core-bound apply function is attached.
 */
export function createDeferredLocaleHandler(effects: {
  setCookie: (locale: string) => void;
  /** non-null in dynamic-only mode — navigation replaces in-place apply */
  navigate: ((locale: string) => void) | null;
}): {
  onChange: (locale: string) => Promise<void>;
  attach: (apply: (locale: string) => Promise<void>) => void;
} {
  let applyLocale: ((locale: string) => Promise<void>) | null = null;
  let pendingLocale: string | null = null;
  return {
    onChange: async (locale) => {
      effects.setCookie(locale);
      if (effects.navigate) {
        effects.navigate(locale);
        return;
      }
      if (applyLocale) {
        await applyLocale(locale);
        return;
      }
      pendingLocale = locale;
    },
    attach: (apply) => {
      applyLocale = apply;
      if (pendingLocale !== null) {
        const replay = pendingLocale;
        pendingLocale = null;
        void apply(replay).catch(() => {});
      }
    },
  };
}

/**
 * Run `fn` once the DOM is parsed — immediately if it already is. Guards the
 * DOM-writing steps (switcher render, translatePage/observe) so a snippet
 * booted from `<head>` doesn't run them against a null `document.body`.
 * Network preload and `build()` are NOT gated — only DOM writes wait.
 */
export function onDomReady(fn: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

export interface LionRapidInstance {
  t: (key: string, params?: Record<string, unknown>, defaultValue?: string) => string;
  tHtml: (key: string, params?: Record<string, unknown>, defaultValue?: string) => string;
  changeLanguage: (locale: string) => Promise<void>;
  getCurrentLocale: () => string;
  on: (event: string, callback: (payload: unknown) => void) => void;
  retranslate: () => void;
  reloadTranslations: () => Promise<void>;
  autoTranslator: AutoTranslator;
  languageSwitcher: LanguageSwitcher;
}

/**
 * Parse script tag attributes
 */
function parseConfig(script: HTMLScriptElement): AutoInitConfig {
  const apiKey = script.dataset.apiKey || '';
  const baseUrl = script.dataset.baseUrl || DEFAULT_API_BASE_URL;
  const defaultLocale = script.dataset.defaultLocale || 'en';
  const namespace = script.dataset.namespace || 'default';
  const languages = (script.dataset.languages || defaultLocale)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const excludeSelectors = (script.dataset.exclude || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const autoDetect = script.dataset.autoDetect === 'true';
  const mode = (script.dataset.mode === 'dynamic-only' ? 'dynamic-only' : 'full') as AutoInitConfig['mode'];
  const switcherStyle = script.dataset.switcher && script.dataset.switcher !== 'false'
    ? script.dataset.switcher
    : undefined;
  const switcherPosition = script.dataset.switcherPosition || undefined;
  const switcherShowFlags = script.dataset.switcherShowFlags !== 'false';
  const switcherShowNames = script.dataset.switcherShowNames === 'true';
  const switcherCustomCss = script.dataset.switcherCustomCss || undefined;
  const editorCdnUrl = script.dataset.editorCdn || undefined;
  const offline = script.dataset.offline !== 'false';

  return {
    apiKey,
    baseUrl,
    defaultLocale,
    namespace,
    languages,
    excludeSelectors,
    autoDetect,
    mode,
    switcherStyle,
    switcherPosition,
    switcherShowFlags,
    switcherShowNames,
    switcherCustomCss,
    editorCdnUrl,
    offline,
  };
}

/**
 * Read the lionrapid_locale cookie and resolve it against the available
 * languages (full BCP-47 tags preserved — `pt-BR` is not chopped to `pt`).
 */
function readLocaleCookie(availableLanguages: string[]): string | null {
  if (typeof document === 'undefined') return null;
  // Anchored to a cookie boundary — a foreign cookie merely containing the
  // name (e.g. `not_lionrapid_locale=de`) must not win.
  const match = document.cookie.match(
    /(?:^|;\s*)lionrapid_locale=([a-z]{2,3}(?:-[a-z0-9]{2,4})*)/i,
  );
  if (!match) return null;
  return matchLocale([match[1]], availableLanguages, '') || null;
}

/**
 * Detect the browser's preferred language via BCP-47 negotiation.
 * `navigator.languages` is already preference-ordered; full tags survive.
 */
function detectBrowserLanguage(availableLanguages: string[], defaultLocale: string): string {
  if (typeof navigator === 'undefined') return defaultLocale;

  const requested =
    navigator.languages && navigator.languages.length > 0
      ? [...navigator.languages]
      : [
          navigator.language ||
            (navigator as Navigator & { userLanguage?: string }).userLanguage ||
            '',
        ];

  return matchLocale(requested, availableLanguages, defaultLocale);
}

/**
 * Build a locale-prefixed URL (pure — no navigation). Strips an existing
 * locale prefix only when it's a known language, so a path like `/about-us/`
 * is never mistaken for a locale; full BCP-47 prefixes (`/pt-BR/…`) are handled.
 * The default locale is served un-prefixed.
 */
export function buildLocaleUrl(
  pathname: string,
  search: string,
  locale: string,
  defaultLocale: string,
  availableLanguages: string[],
): string {
  const segments = pathname.split('/'); // ['', 'pt-BR', 'about']
  const first = segments[1] ?? '';
  const hasLocalePrefix =
    LOCALE_TAG_REGEX.test(first) &&
    matchLocale([first], availableLanguages, '') !== '';
  const path = hasLocalePrefix ? '/' + segments.slice(2).join('/') : pathname;
  const prefix = locale === defaultLocale ? '' : '/' + locale;
  return prefix + (path || '/') + search;
}

/** Navigate to a locale-prefixed URL (dynamic-only mode). */
function navigateToLocale(
  locale: string,
  defaultLocale: string,
  availableLanguages: string[],
): void {
  window.location.href = buildLocaleUrl(
    window.location.pathname,
    window.location.search,
    locale,
    defaultLocale,
    availableLanguages,
  );
}

/**
 * Resolve the initial locale from the environment, preserving full BCP-47
 * tags. Dynamic-only trusts the server-set `<html lang>`; full mode uses
 * cookie → browser negotiation → default. Extracted so the precedence matrix
 * is testable without booting `autoInit`.
 */
export function resolveInitialLocale(config: AutoInitConfig): string {
  if (config.mode === 'dynamic-only') {
    const htmlLang =
      typeof document !== 'undefined' ? document.documentElement.lang : '';
    return htmlLang ? normalizeLocale(htmlLang) : config.defaultLocale;
  }
  const cookieLocale = readLocaleCookie(config.languages);
  if (cookieLocale) return cookieLocale;
  if (config.autoDetect) {
    return detectBrowserLanguage(config.languages, config.defaultLocale);
  }
  return config.defaultLocale;
}

/** Reflect the locale's text direction on `<html dir>` (RTL languages). */
export function applyLocaleDir(locale: string): void {
  if (typeof document !== 'undefined') {
    document.documentElement.dir = localeDir(locale);
  }
}

/**
 * The `<script data-api-key>` tag that configures this boot. Prefer the
 * script that is actually executing (valid here because autoInit runs at
 * script-eval time), so a page with several snippet tags configures from
 * the right one; fall back to the last matching tag when currentScript is
 * unavailable (module contexts, async boundaries) — flagged, since with two
 * or more candidates "last wins" is a silent guess.
 */
export function findConfigScript(): HTMLScriptElement | undefined {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement && current.dataset.apiKey) {
    return current;
  }
  const scripts = document.querySelectorAll('script[data-api-key]');
  if (scripts.length > 1) {
    console.warn(
      `[LionRapid] ${scripts.length} script tags with data-api-key found; using the last one`,
    );
  }
  return scripts[scripts.length - 1] as HTMLScriptElement | undefined;
}

/**
 * Initialize LionRapid from script tag
 */
async function autoInit(): Promise<void> {
  const script = findConfigScript();

  if (!script) {
    console.warn('[LionRapid] No script tag with data-api-key found');
    return;
  }

  const config = parseConfig(script);
  // Mirror config for the editor bundle, but never expose the key on window.
  window.__lionrapidConfig = configForWindow(config);

  if (!config.apiKey) {
    console.warn('[LionRapid] Missing data-api-key attribute');
    return;
  }

  // Hard gate (Phase 18): only a publishable pk_ key boots the network
  // layer. A non-pk_ key is a secret shipped to the page — refuse to put it
  // on the wire; cached/offline translations still apply.
  const writePolicy = resolveWritePolicy(config.apiKey);
  if (writePolicy.warnRefused) {
    console.error(
      '[LionRapid] data-api-key must be a publishable pk_ key — network ' +
        'translations are disabled. Copy the snippet from the integration ' +
        'hub to get the pk_ key for this project.',
    );
  }

  // Determine initial locale
  const initialLocale = resolveInitialLocale(config);

  // Reflect text direction for RTL locales on <html dir> before first paint.
  applyLocaleDir(initialLocale);

  // Create language switcher immediately (no core dependency for rendering)
  const languageSwitcher = new LanguageSwitcher();
  languageSwitcher.setLanguages(config.languages);
  languageSwitcher.setInitialLocale(initialLocale);

  // Render switcher as soon as DOM is ready (don't wait for network)
  const renderSwitcher = () => {
    // In full mode, auto-create floating container from script tag config
    // In dynamic-only mode, server (WP/edge) provides the container via HTML
    if (config.mode !== 'dynamic-only') {
      if (config.switcherStyle && !document.querySelector('[data-lionrapid-switcher]')) {
        const container = document.createElement('div');
        container.dataset.lionrapidSwitcher = config.switcherStyle;
        if (config.switcherPosition) {
          container.dataset.lionrapidSwitcherPosition = config.switcherPosition;
        }
        if (!config.switcherShowFlags) {
          container.dataset.lionrapidSwitcherShowFlags = 'false';
        }
        if (!config.switcherShowNames) {
          container.dataset.lionrapidSwitcherShowNames = 'false';
        }
        if (config.switcherCustomCss) {
          container.dataset.lionrapidSwitcherCustomCss = config.switcherCustomCss;
        }
        document.body.appendChild(container);
      }
    }
    // Always init: injects CSS, hydrates any [data-lionrapid-switcher] elements
    languageSwitcher.initAll();
  };

  onDomReady(renderSwitcher);

  // Language changes are accepted from the first paint: cookie + dynamic-only
  // navigation immediately, in-place retranslation replayed once core is up.
  const localeHandler = createDeferredLocaleHandler({
    setCookie: (locale) => {
      document.cookie = `lionrapid_locale=${locale}; path=/; max-age=31536000; SameSite=Lax`;
    },
    navigate:
      config.mode === 'dynamic-only'
        ? (locale) => navigateToLocale(locale, config.defaultLocale, config.languages)
        : null,
  });
  languageSwitcher.setOnLanguageChange(localeHandler.onChange);

  try {
    // Build LionRapid with all cache layers
    const builder = LionRapidBuilder.init({
      defaultLocale: initialLocale,
      namespace: config.namespace,
    })
      .use(new MemoryRepository({ enabled: true }))
      .use(new LocalStorageRepository({ enabled: true }))
      .use(new CacheStorageRepository({ enabled: true }));

    // Offline resilience floor: a never-expiring "last known good" cache layer,
    // hydrated by successful fetches. Checked AFTER the TTL'd caches and BEFORE
    // the network, so the site keeps translating with the server down even once
    // the normal caches expire.
    //   - ttl must be huge — the handlers treat ttl=0 as "expire immediately".
    //   - a DISTINCT cacheName is essential: CacheStorageRepository keys by
    //     cacheName (it ignores `prefix`), so without this the floor would share
    //     the default store and the 7d cache's expiry would delete its entries.
    if (config.offline !== false) {
      builder.use(
        new CacheStorageRepository({
          enabled: true,
          options: {
            ttl: Number.MAX_SAFE_INTEGER,
            cacheName: CORE_CACHE_NAMES.offlineFloor,
          },
        })
      );
    }

    // Phase 18: the network layer exists only for pk_ keys — a refused key
    // boots caches/offline layers alone and never reaches the wire.
    if (writePolicy.bootNetwork) {
      builder.use(
        new NetworkRepository({
          enabled: true,
          options: {
            baseUrl: config.baseUrl,
            apiKey: config.apiKey,
            // pk_ keys sync missing source strings (discovery) — see
            // resolveWritePolicy.
            syncMissing: writePolicy.syncMissing,
            preloadNamespaces: [
              {
                locale: initialLocale,
                namespace: config.namespace,
                eager: true,
              },
            ],
          },
        })
      );
    }

    const core = await builder.build();

    // Create auto translator
    const autoTranslator = new AutoTranslator({
      excludeSelectors: config.excludeSelectors,
    });
    autoTranslator.setCore(core);
    autoTranslator.init({
      namespace: config.namespace,
      locale: initialLocale,
      sourceLocale: config.defaultLocale,
    });

    // Wire up core to switcher now that it's ready; a click that arrived
    // during build() replays here.
    languageSwitcher.setCore(core);
    localeHandler.attach(async (locale) => {
      await core.changeLanguage(locale, {
        preloadNamespaces: [config.namespace],
      });
      applyLocaleDir(locale);
      autoTranslator.updateLocale(locale);
      autoTranslator.retranslate();
    });

    // === Reactive translation reload ===
    const RETRY_DELAYS = [2000, 8000, 30000]; // backoff: 2s, 8s, 30s
    const REVALIDATE_STALE_MS = 60_000; // 60s before background revalidation
    let hasPendingTranslations = false;
    let retryTimers: ReturnType<typeof setTimeout>[] = [];
    let lastRevalidatedAt = Date.now();

    const reloadTranslations = async (): Promise<void> => {
      await core.reloadNamespace(config.namespace);
      autoTranslator.translatePage();
      lastRevalidatedAt = Date.now();
    };

    const cancelRetries = () => {
      retryTimers.forEach(t => clearTimeout(t));
      retryTimers = [];
    };

    // After sync: schedule backoff retries
    core.bus.on('sync:batch:complete', () => {
      hasPendingTranslations = true;
      cancelRetries();
      RETRY_DELAYS.forEach(delay => {
        retryTimers.push(
          setTimeout(() => reloadTranslations().catch(() => {}), delay)
        );
      });
    });

    // On tab focus: reload if pending, or background revalidate if stale (>60s)
    // The revalidation is cheap — server returns 304 if nothing changed
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;

      if (hasPendingTranslations) {
        hasPendingTranslations = false;
        cancelRetries();
        reloadTranslations().catch(() => {});
      } else if (Date.now() - lastRevalidatedAt > REVALIDATE_STALE_MS) {
        reloadTranslations().catch(() => {});
      }
    });

    // Expose global API
    const instance: LionRapidInstance = {
      t: (key, params, defaultValue) => core.t(key, params || {}, defaultValue),
      tHtml: (key, params, defaultValue) => core.tHtml(key, params || {}, defaultValue),
      changeLanguage: async (locale) => {
        await core.changeLanguage(locale, {
          preloadNamespaces: [config.namespace],
        });
        applyLocaleDir(locale);
        autoTranslator.updateLocale(locale);
        autoTranslator.retranslate();
        languageSwitcher.initAll();
      },
      getCurrentLocale: () => core.getCurrentLocale(),
      on: (event, callback) => core.bus.on(event as keyof typeof core.bus, callback),
      retranslate: () => autoTranslator.retranslate(),
      reloadTranslations,
      autoTranslator,
      languageSwitcher,
    };

    window.LionRapid = instance;

    // Detect editor mode (snippet case). Edge proxy and WordPress inject the
    // editor server-side, so skip if either kind of editor script is already
    // present to avoid double-loading.
    const editorParam = new URLSearchParams(window.location.search).get('__lr_editor');
    if (editorParam && !document.querySelector('script[data-grant], script[data-lr-editor]')) {
      const editorScript = document.createElement('script');
      editorScript.src = config.editorCdnUrl
        || 'https://cdn.lionrapid.com/lionrapid.editor.min.js';
      editorScript.dataset.grant = editorParam;
      editorScript.dataset.locale = core.getCurrentLocale();
      editorScript.dataset.api = config.baseUrl;
      editorScript.async = true;
      document.body.appendChild(editorScript);
    }

    // Start auto-translation once the DOM is ready — a <head>-script boot
    // would otherwise run translatePage()/observe() against a null
    // document.body. Core build + network preload already ran above.
    onDomReady(() => {
      if (config.mode !== 'dynamic-only') {
        autoTranslator.translatePage();
      }
      autoTranslator.observe();
    });
  } catch (error) {
    console.error('[LionRapid] Initialization failed:', error);
  }
}

// Auto-run on script load
autoInit();

export { autoInit, parseConfig };
